import type { AxiosError } from 'axios';
import axios from 'axios';
import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  startTransition,
} from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import ConnectionErrorDialog from '@/components/ConnectionErrorDialog';
import api, { isConnectionError } from '@/utils/axiosInterceptor';

interface ConnectionContextType {
  isConnected: boolean;
  isShowingError: boolean;
  isShowingSuccess: boolean;
  isThrottled: boolean;
  checkConnection: (
    isManualCheck?: boolean,
    showToast?: boolean,
  ) => Promise<boolean>;
  showConnectionError: () => void;
  hideConnectionError: () => void;
  showConnectionSuccess: () => void;
  hideConnectionSuccess: () => void;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(
  undefined,
);

// Provider Component
export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(true);
  const [isShowingError, setIsShowingError] = useState(false);
  const [isShowingSuccess, setIsShowingSuccess] = useState(false);
  const [isThrottled, setIsThrottled] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [lastAutoCheckTime, setLastAutoCheckTime] = useState(0);
  const [lastManualCheckTime, setLastManualCheckTime] = useState(0);
  const location = useLocation();

  // Don't show connection errors on login page
  const isLoginPage = location.pathname === '/login';

  // Function to show the connection error dialog
  const showConnectionError = useCallback(() => {
    if (!isLoginPage) {
      startTransition(() => {
        setIsShowingError(true);
      });
    } else {
      // On login page, only show toast notification
      toast.error('❌ فشل الاتصال بالخادم! 🔌 ⚠️', {
        duration: 4000,
      });
    }
  }, [isLoginPage]);

  // Function to hide the connection error dialog
  const hideConnectionError = useCallback(() => {
    startTransition(() => {
      setIsShowingError(false);
    });
  }, []);

  // Function to show the connection success dialog
  const showConnectionSuccess = useCallback(() => {
    toast.success('✅ تم الاتصال بالخادم بنجاح! 🌐 🚀', {
      duration: 3000,
    });
    startTransition(() => {
      setIsShowingSuccess(true);
    });
  }, []);

  // Function to hide the connection success dialog
  const hideConnectionSuccess = useCallback(() => {
    startTransition(() => {
      setIsShowingSuccess(false);
    });
  }, []);

  // Function to check connection status with throttling
  const checkConnection = useCallback(
    async (isManualCheck = false, showToast = true): Promise<boolean> => {
      // If already checking, return current status
      if (checkingConnection) {
        return isConnected;
      }

      const now = Date.now();

      // Separate throttling for manual and auto checks
      if (isManualCheck) {
        const MANUAL_THROTTLE_TIME = 3000; // 3s for manual checks
        if (now - lastManualCheckTime < MANUAL_THROTTLE_TIME) {
          // Set throttled state for UI feedback
          startTransition(() => {
            setIsThrottled(true);
          });
          setTimeout(
            () => startTransition(() => setIsThrottled(false)),
            MANUAL_THROTTLE_TIME - (now - lastManualCheckTime),
          );
          return isConnected;
        }
        setLastManualCheckTime(now);
      } else {
        const AUTO_THROTTLE_TIME = 30000; // 30s for auto checks
        if (now - lastAutoCheckTime < AUTO_THROTTLE_TIME) {
          return isConnected;
        }
        setLastAutoCheckTime(now);
      }

      try {
        startTransition(() => {
          setCheckingConnection(true);
        });

        let connectionSuccessful = false;

        try {
          // Extract base URL without the /v1 path
          const baseUrl =
            api.defaults.baseURL?.split('/v1')[0] ||
            import.meta.env.VITE_HEALTHY_EMPIRE_SERVER ||
            'https://10.10.1.50:3443';

          // Simple HEAD request to check if server is reachable
          await axios.head(`${baseUrl}/v1/auth/health`, { timeout: 3000 });

          // If HEAD request succeeds, server is reachable
          connectionSuccessful = true;
        } catch {
          // If HEAD request fails, try health endpoint as fallback
          try {
            const response = await api.get('/auth/health', { timeout: 5000 });
            connectionSuccessful =
              response.status === 200 && response.data?.status === 'ok';
          } catch (error) {
            // Properly type axios errors
            const axiosError = error as AxiosError;

            // If we get a 429, consider it as connected but rate limited
            if (axiosError.response?.status === 429) {
              connectionSuccessful = true;

              // Set throttled state with timeout to auto-reset
              startTransition(() => {
                setIsThrottled(true);
              });
              setTimeout(
                () => startTransition(() => setIsThrottled(false)),
                5000,
              );
            } else {
              connectionSuccessful = false;
            }
          }
        }

        // Update connection state
        startTransition(() => {
          setIsConnected(connectionSuccessful);
        });

        // Show success toast for manual checks when connection is good
        if (isManualCheck && connectionSuccessful && showToast) {
          toast.success('✅ تم الاتصال بالخادم بنجاح! 🌐 🚀', {
            duration: 3000,
          });
        } else if (isManualCheck && !connectionSuccessful && showToast) {
          // Show error toast for manual checks
          toast.error('❌ فشل الاتصال بالخادم! 🔌 ⚠️', {
            duration: 4000,
          });

          // Also show error dialog for connection failures in manual checks
          showConnectionError();
        }

        return connectionSuccessful;
      } finally {
        startTransition(() => {
          setCheckingConnection(false);
        });
      }
    },
    [
      checkingConnection,
      isConnected,
      lastAutoCheckTime,
      lastManualCheckTime,
      showConnectionError,
    ],
  );

  // Automatically check connection periodically - NOT on mount to avoid suspense issues
  useEffect(() => {
    let isMounted = true;
    const CHECK_INTERVAL = 60000; // 1 minute

    // Set up periodic checks - no initial check to avoid suspense issues
    const intervalId = setInterval(() => {
      if (isMounted) {
        // Wrap in try-catch to prevent unhandled errors
        try {
          checkConnection(false, false).catch(() => {
            // Silently catch errors in automatic check
          });
        } catch (error) {
          // Catch any synchronous errors too
          console.error('Error in connection check:', error);
        }
      }
    }, CHECK_INTERVAL);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [checkConnection]);

  // Special check for login page - with proper error handling
  useEffect(() => {
    let isMounted = true;

    if (isLoginPage && isMounted) {
      // Small delay before checking connection on login page
      const loginCheckTimeout = setTimeout(() => {
        if (isMounted) {
          try {
            checkConnection(true, true).catch(() => {
              // Silently catch errors
            });
          } catch (error) {
            console.error('Error in login page connection check:', error);
          }
        }
      }, 2000);

      return () => {
        isMounted = false;
        clearTimeout(loginCheckTimeout);
      };
    }
  }, [isLoginPage, checkConnection]);

  // Set up global error interceptor for detecting connection issues in real-time
  useEffect(() => {
    let lastErrorTime = 0;
    const ERROR_CHECK_COOLDOWN = 5000; // 5 seconds

    const axiosInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const now = Date.now();
        if (now - lastErrorTime < ERROR_CHECK_COOLDOWN) {
          return Promise.reject(error);
        }
        lastErrorTime = now;

        try {
          if (isConnectionError(error) && !isLoginPage) {
            startTransition(() => {
              setIsConnected(false);
            });
            showConnectionError();
          }
        } catch (handlerError) {
          console.error('Error in connection error handler:', handlerError);
        }

        return Promise.reject(error);
      },
    );

    return () => {
      // Remove the interceptor when the component unmounts
      axios.interceptors.response.eject(axiosInterceptor);
    };
  }, [isLoginPage, showConnectionError]);

  // Automatically check connection while error dialog is open
  useEffect(() => {
    if (!isShowingError) return;

    let isMounted = true;
    const AUTO_CHECK_INTERVAL = 5000; // Check every 5 seconds while dialog is open

    // Immediate check when dialog opens
    const checkAndClose = async () => {
      if (!isMounted) return;

      try {
        const connected = await checkConnection(false, false);

        if (connected && isMounted) {
          // Connection restored - close dialog and show success
          hideConnectionError();
          toast.success('✅ تم استعادة الاتصال بالخادم! 🌐 🚀', {
            duration: 3000,
          });

          // Update state
          startTransition(() => {
            setIsConnected(true);
          });
        }
      } catch (error) {
        console.error('Error during auto-check:', error);
      }
    };

    // Check immediately
    checkAndClose();

    // Then check periodically
    const intervalId = setInterval(checkAndClose, AUTO_CHECK_INTERVAL);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [isShowingError, checkConnection, hideConnectionError]);

  // Handle retry action
  const handleRetry = useCallback(async () => {
    hideConnectionError();

    try {
      const connected = await checkConnection(true); // Mark as manual check

      if (!connected && !isLoginPage) {
        // If still not connected, show the dialog again (but not on login page)
        setTimeout(showConnectionError, 500);
      }
    } catch (error) {
      console.error('Error during connection retry:', error);
    }
  }, [checkConnection, hideConnectionError, isLoginPage, showConnectionError]);

  return (
    <ConnectionContext.Provider
      value={{
        isConnected,
        isShowingError,
        isShowingSuccess,
        isThrottled,
        checkConnection,
        showConnectionError,
        hideConnectionError,
        showConnectionSuccess,
        hideConnectionSuccess,
      }}
    >
      {children}
      <ConnectionErrorDialog isOpen={isShowingError} onRetry={handleRetry} />
    </ConnectionContext.Provider>
  );
}

// Custom Hook to use Connection Context
export const useConnection = () => {
  const context = useContext(ConnectionContext);
  if (!context) {
    throw new Error('useConnection must be used within a ConnectionProvider');
  }
  return context;
};
